// external dependencies
import React, { useState, useMemo } from 'react'
import styled from '@emotion/styled'
import { array, string } from 'prop-types'

// utils
import { convertArrOfObjToArrOfObjs } from '@jelly/utils'

// components
import { NearYouSearch } from './NearYouSearch'
import { NearYouService } from './NearYouService'
import { NearYouMap } from './NearYouMap'

const Column = styled.div`
	border-right-style: solid;
	border-right-width: 1px;
	display: flex;
	flex-direction: column;
	width: 16rem;
`

const MapWrapper = styled.div`
	border-style: solid;
	border-width: 1px;
	display: flex;
	height: 75vh;
	margin-bottom: 2rem;
`

const Search = styled.div`
	background-color: #fff;
	padding: 0.5rem;
`

const Services = styled.div`
	overflow-y: auto;
`

NearYou.propTypes = {
	guid: string,
	services: array,
}

const flattenServices = (arr) => {
	const flattenedData = arr
		.filter((service) => service.company) // filter based on company data
		.map((item) => {
			const {
				id,
				company: { description = '', geos, name, phone, url },
				thumbnail,
			} = item

			const { addressLine1, addressLine2, city, country, latitude, longitude, postal, province } = geos[0]

			return {
				address: `${addressLine1} ${addressLine2}`,
				affiliateLink: url,
				city,
				country: country,
				description: description,
				displayUrl: url,
				id,
				lat: latitude,
				lng: longitude,
				logo: null,
				logoUrl: thumbnail,
				monetization: { type: 'widget', widgetId: '10062' },
				name: name,
				phone: phone,
				state: province,
				zip: postal,
			}
		})

	return flattenedData
}

const convertToFlattenedServices = (data) => {
	const servicesArrOfObjs = convertArrOfObjToArrOfObjs(data)
	const flattenedServices = flattenServices(servicesArrOfObjs)
	return flattenedServices
}

export function NearYou({ guid, services }) {
	const [center, setCenter] = useState({})

	// Wrapped in useMemo to prevent unnecessary recalculations when the services haven't change
	const memoizedTransformedServices = useMemo(() => {
		return convertToFlattenedServices(services)
	}, [services])

	/**
	 * Whenever the user selects a new place from the google map searchbox, update the center of the map to be rendered
	 *
	 * @param  {array} returnedArr :: Values returned by the Google Places API matching user's search
	 */
	const onPlacesChanged = (returnedArr) => {
		if (returnedArr.length > 0) {
			setCenter({
				lat: returnedArr[0].geometry.location.lat(),
				lng: returnedArr[0].geometry.location.lng(),
			})
		}
	}

	return (
		<MapWrapper>
			<Column>
				<Search>
					<NearYouSearch onPlacesChanged={onPlacesChanged} />
				</Search>
				<Services>
					{memoizedTransformedServices
						.filter((service) => service.lat && service.lng)
						.map((service) => (
							<NearYouService guid={guid} key={service.id} {...service} />
						))}
				</Services>
			</Column>
			<NearYouMap center={center} guid={guid} services={memoizedTransformedServices} />
		</MapWrapper>
	)
}
